import { TaxCalculationResult, payPeriodLabels, payPeriodMultipliers, type PayPeriod } from "@shared/schema";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TaxBreakdownChart, TaxBreakdownList } from "./TaxBreakdownChart";
import { TrendingDown, TrendingUp, Percent } from "lucide-react";

interface ResultsDisplayProps {
  result: TaxCalculationResult | null;
  displayPeriod: PayPeriod;
  isLoading?: boolean;
}

export function ResultsDisplay({ result, displayPeriod, isLoading }: ResultsDisplayProps) {
  if (isLoading) {
    return <ResultsSkeleton />;
  }

  if (!result) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center text-muted-foreground">
            <p className="text-lg mb-2">Enter your salary to see results</p>
            <p className="text-sm">Select a country and enter your gross salary</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const symbol = result.country.symbol;
  const multiplier = payPeriodMultipliers[displayPeriod];
  const originalMultiplier = payPeriodMultipliers[result.payPeriod];
  const conversionFactor = originalMultiplier / multiplier;

  const displayGross = result.grossSalary * conversionFactor;
  const displayNet = result.netSalary * conversionFactor;
  const displayTax = result.totalTax * conversionFactor;

  const formatCurrency = (amount: number) => {
    return `${symbol}${amount.toLocaleString("en-US", { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    })}`;
  };

  const formatLargeCurrency = (amount: number) => {
    return `${symbol}${amount.toLocaleString("en-US", { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  const adjustedBreakdown = result.breakdown.map(item => ({
    ...item,
    amount: item.amount * conversionFactor,
    percentage: item.percentage
  }));

  return (
    <div className="space-y-6 overflow-hidden">
      <Card className="overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium text-muted-foreground flex items-center gap-2">
            <span className="text-2xl">{result.country.flag}</span>
            Your Take-Home Pay
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground mb-1">
              Per {payPeriodLabels[displayPeriod]}
            </p>
            <p 
              className="text-4xl sm:text-5xl md:text-6xl font-bold font-mono tracking-tight text-success truncate"
              data-testid="text-net-salary"
            >
              {formatLargeCurrency(displayNet)}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 py-4 border-y border-border">
            <div className="text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                Gross
              </p>
              <p className="text-lg font-mono font-semibold" data-testid="text-gross-salary">
                {formatCurrency(displayGross)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                Tax
              </p>
              <p className="text-lg font-mono font-semibold text-destructive" data-testid="text-total-tax">
                -{formatCurrency(displayTax)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                Tax Rate
              </p>
              <p className="text-lg font-mono font-semibold" data-testid="text-tax-rate">
                {result.effectiveTaxRate.toFixed(1)}%
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50">
            <div className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium">Effective Tax Rate</span>
            </div>
            <span className="text-2xl font-bold font-mono">
              {result.effectiveTaxRate.toFixed(1)}%
            </span>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle className="text-lg">Tax Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="overflow-hidden">
          <TaxBreakdownChart 
            breakdown={adjustedBreakdown}
            grossSalary={displayGross}
            currencySymbol={symbol}
          />
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle className="text-lg">Detailed Summary</CardTitle>
        </CardHeader>
        <CardContent className="overflow-hidden">
          <TaxBreakdownList 
            breakdown={adjustedBreakdown}
            currencySymbol={symbol}
          />
          <div className="mt-4 pt-4 border-t border-border flex justify-between items-center">
            <span className="font-semibold">Net Take-Home</span>
            <span className="text-xl font-bold font-mono text-success">
              {formatCurrency(displayNet)}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ResultsSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2">
          <div className="h-6 w-40 bg-muted animate-pulse rounded" />
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center py-4">
            <div className="h-4 w-20 mx-auto bg-muted animate-pulse rounded mb-2" />
            <div className="h-16 w-64 mx-auto bg-muted animate-pulse rounded" />
          </div>
          <div className="grid grid-cols-3 gap-4 py-4 border-y border-border">
            {[1, 2, 3].map(i => (
              <div key={i} className="text-center">
                <div className="h-3 w-12 mx-auto bg-muted animate-pulse rounded mb-2" />
                <div className="h-6 w-20 mx-auto bg-muted animate-pulse rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="h-6 w-32 bg-muted animate-pulse rounded" />
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="space-y-2">
              <div className="flex justify-between">
                <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
              </div>
              <div className="h-3 w-full bg-muted animate-pulse rounded" />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
